import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AxiosError } from 'axios';
import {
    fetchTimesheetsForApprovalAsync,
    approveTimesheetsAsync,
    rejectTimesheetAsync,
} from '../../../actions/TimesheetActions';
import type { TTimesheet, TApprovalErrorType } from '../../../types/timesheet';
import type { TApiResponse } from '../../../types/api';

type PageState = 'loading' | 'loaded' | 'error';

type UseTimesheetApprovalOptions = {
    token?: string;
    onApproveSuccess?: (count: number) => void;
    onApproveError?: (message?: string) => void;
    onRejectSuccess?: () => void;
    onRejectError?: (message?: string) => void;
    onLoadError?: (errorType: TApprovalErrorType, errorMessage: string) => void;
};

export function useTimesheetApproval(options: UseTimesheetApprovalOptions = {}) {
    const dispatch = useDispatch();
    const { token, onApproveSuccess, onApproveError, onRejectSuccess, onRejectError, onLoadError } = options;

    const [pageState, setPageState] = useState<PageState>('loading');
    const [timesheets, setTimesheets] = useState<TTimesheet[]>([]);
    const [clientName, setClientName] = useState<string>('');
    const [errorType, setErrorType] = useState<TApprovalErrorType>('general');
    const [errorMessage, setErrorMessage] = useState<string>('');

    const handleError = useCallback((error: AxiosError<TApiResponse<unknown>>) => {
        let type: TApprovalErrorType = 'general';
        let message = 'An error occurred';

        if (error.response) {
            const { status, data } = error.response;
            const errorCode = data?.meta?.error_code;

            if (status === 401 || status === 403 || errorCode === 'ERR_TOKEN_INVALID') {
                type = 'invalid';
                message = token ? 'Invalid or unauthorized approval link' : 'You do not have permission to access this page';
            } else if (status === 410 || errorCode === 'ERR_TOKEN_EXPIRED') {
                type = 'expired';
                message = 'This approval link has expired';
            } else {
                type = 'general';
                message = data?.meta?.detail || 'An error occurred';
            }
        } else if (error.message === 'Network Error') {
            type = 'network';
            message = 'Network error. Please check your connection';
        } else {
            type = 'general';
            message = error.message || 'An error occurred';
        }

        setErrorType(type);
        setErrorMessage(message);
        setPageState('error');

        if (onLoadError) {
            onLoadError(type, message);
        }
    }, [onLoadError, token]);

    const loadTimesheets = useCallback(async() => {
        setPageState('loading');

        try {
            await dispatch(
                fetchTimesheetsForApprovalAsync(
                    token,
                    data => {
                        setTimesheets(data.timesheets || []);
                        setClientName(data.client_name || '');
                        setPageState('loaded');
                    },
                    error => {
                        handleError(error);
                    },
                ),
            );
        } catch (error) {
            handleError(error as AxiosError<TApiResponse<unknown>>);
        }
    }, [dispatch, handleError, token]);

    const handleApprove = useCallback(async(timesheetIds: number[]) => {
        try {
            await dispatch(
                approveTimesheetsAsync(
                    timesheetIds,
                    () => {
                        if (onApproveSuccess) {
                            onApproveSuccess(timesheetIds.length);
                        }
                    },
                    (error, message) => {
                        if (onApproveError) {
                            onApproveError(message);
                        }
                    },
                    token,
                ),
            );
        } catch (error) {
            if (onApproveError) {
                onApproveError();
            }
        } finally {
            // Always refetch timesheets to get latest data
            loadTimesheets();
        }
    }, [dispatch, loadTimesheets, onApproveError, onApproveSuccess, token]);

    const handleReject = useCallback(async(timesheetId: number, reason: string) => {
        try {
            await dispatch(
                rejectTimesheetAsync(
                    timesheetId,
                    reason,
                    () => {
                        if (onRejectSuccess) {
                            onRejectSuccess();
                        }
                    },
                    (error, message) => {
                        if (onRejectError) {
                            onRejectError(message);
                        }
                    },
                    token,
                ),
            );
        } catch (error) {
            if (onRejectError) {
                onRejectError();
            }
        } finally {
            // Always refetch timesheets to get latest data
            loadTimesheets();
        }
    }, [dispatch, loadTimesheets, onRejectError, onRejectSuccess, token]);

    return {
        pageState,
        timesheets,
        clientName,
        errorType,
        errorMessage,
        loadTimesheets,
        handleApprove,
        handleReject,
    };
}
